import { FilterDropdown, TextField, useSelect, useTable } from "@refinedev/antd";
import { Button, Select, Space, Table, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ILicence, IVessel } from "interfaces";
import React, { useState } from "react";

const { Text } = Typography;

interface ServiceDetail {
  key: string;
  service: string;
  type: string;
  frequency: string;
}

const getActiveServices = (record: ILicence): string[] => {
  const services: string[] = [];
  if (record.subscription?.metadataAvcs) services.push("AVCS");
  if (record.subscription?.metadataAdp) services.push("ADP");
  if (record.subscription?.metadataEnp) services.push("e-NP");
  return services;
};

const getServiceDetails = (record: ILicence): ServiceDetail[] => {
  const details: ServiceDetail[] = [];
  if (record.subscription?.metadataAvcs) {
    details.push({
      key: "avcs",
      service: "AVCS",
      type: record.subscription.metadataAvcs.subscriptionType || "-",
      frequency: record.subscription.metadataAvcs.paymentFrequency || "-",
    });
  }
  if (record.subscription?.metadataAdp) {
    details.push({
      key: "adp",
      service: "ADP",
      type: record.subscription.metadataAdp.subscriptionType || "-",
      frequency: record.subscription.metadataAdp.paymentFrequency || "-",
    });
  }
  if (record.subscription?.metadataEnp) {
    details.push({
      key: "enp",
      service: "e-NP",
      type: record.subscription.metadataEnp.subscriptionType || "-",
      frequency: record.subscription.metadataEnp.paymentFrequency || "-",
    });
  }
  return details;
};

const SERVICE_FILTER_OPTIONS = [
  { label: "AVCS", value: "subscription.metadataAvcs.activationStatus" },
  { label: "ADP", value: "subscription.metadataAdp.activationStatus" },
  { label: "e-NP", value: "subscription.metadataEnp.activationStatus" },
];

export const SubscriptionDetails: React.FC = () => {
  const [selectedService, setSelectedService] = useState<string>();
  const { tableProps, setFilters, filters } = useTable<ILicence>({
    resource: "licence",
    pagination: {
      pageSize: 10
    },
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",
    pagination: {
      mode: "server"
    }
  });

  const expandedRowRender = (record: ILicence) => {
    const data = getServiceDetails(record);
    const nestedColumns: ColumnsType<ServiceDetail> = [
      { title: "Service", dataIndex: "service", key: "service", width: 120 },
      { title: "Subscription Type", dataIndex: "type", key: "type", width: 200 },
      { title: "Billing Frequency", dataIndex: "frequency", key: "frequency", width: 150 },
    ];
    return (
      <Table
        columns={nestedColumns}
        dataSource={data}
        pagination={false}
        size="small"
        rowKey="key"
      />
    );
  };

  const columns: ColumnsType<ILicence> = [
    {
      title: "IMO Number",
      dataIndex: ["vessel", "imoNumber"],
      key: "vessel.imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            placeholder="Select IMO"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["vessel", "name"],
      key: "vessel.name",
      width: 200,
      ellipsis: { showTitle: true },
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            placeholder="Select Vessel"
            {...vesselSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Active Services",
      filterDropdown: ({ confirm, clearFilters }) => (
        <div style={{ padding: 8 }}>
          <Select
            style={{ width: 200, marginBottom: 8, display: "block" }}
            placeholder="Select Service"
            allowClear
            value={selectedService}
            options={SERVICE_FILTER_OPTIONS}
            onChange={(value) => setSelectedService(value)}
          />
          <Space>
            <Button
              type="primary"
              size="small"
              onClick={() => {
                const serviceFields = SERVICE_FILTER_OPTIONS.map(o => o.value);
                const otherFilters = (filters || []).filter(
                  f => !("field" in f && serviceFields.includes(f.field as string))
                );
                if (selectedService) {
                  setFilters([
                    ...otherFilters,
                    { field: selectedService, operator: "eq", value: "active" }
                  ], "replace");
                }
                confirm();
              }}
            >
              Filter
            </Button>
            <Button
              size="small"
              onClick={() => {
                const serviceFields = SERVICE_FILTER_OPTIONS.map(o => o.value);
                const otherFilters = (filters || []).filter(
                  f => !("field" in f && serviceFields.includes(f.field as string))
                );
                setSelectedService(undefined);
                setFilters(otherFilters, "replace");
                clearFilters?.();
              }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      render: (_, record) => {
        const services = getActiveServices(record);
        if (services.length === 0) {
          return <Text type="secondary">None</Text>;
        }
        const colorMap: Record<string, string> = {
          "AVCS": "blue",
          "ADP": "purple",
          "e-NP": "cyan",
        };
        return services.map((s) => (
          <Tag key={s} color={colorMap[s]}>{s}</Tag>
        ));
      },
    },
  ];

  return (
    <Table
      {...tableProps}
      columns={columns}
      rowKey="id"
      size="middle"
      expandable={{
        expandedRowRender,
        rowExpandable: (record) => getActiveServices(record).length > 0,
      }}
      pagination={{
        ...tableProps.pagination,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
      }}
    />
  );
};
